import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import apiService from "../services/apiService";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import EmailConfigModal from "../Components/EmailConfigModal";

/* EXACT COLORS FROM IMAGE */
const BLUE_PRIMARY = "#1f5582";
const BLUE_CARD = "#2e75b6";
const ORANGE_ACCENT = "#ff7f0e";
const WHITE_BG = "#ffffff";
const BORDER_GRAY = "#d0d7e0";
const LIGHT_BG = "#f8fafc";

const MonthlyStatusDashboard = forwardRef(({ 
  selectedApplication = "",
  selectedEnvironment = "",
  selectedMonth: propSelectedMonth,
  setSelectedMonth: propSetSelectedMonth,
  showEmailSubject: propShowEmailSubject,
  setShowEmailSubject: propSetShowEmailSubject,
  emailConfigOpen: propEmailConfigOpen,
  setEmailConfigOpen: propSetEmailConfigOpen,
}, ref) => {
  const dashboardRef = useRef(null);
  const highlightRef = useRef(null);

  const [editMode, setEditMode] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ open: false, message: '', severity: 'info' });
  const [customEmailSubject, setCustomEmailSubject] = useState("");

  // Use props if provided, otherwise use internal state
  const [internalSelectedMonth, setInternalSelectedMonth] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [internalShowEmailSubject, setInternalShowEmailSubject] = useState(false);
  const [internalEmailConfigOpen, setInternalEmailConfigOpen] = useState(false);

  const selectedMonth = propSelectedMonth !== undefined ? propSelectedMonth : internalSelectedMonth;
  const setSelectedMonth = propSetSelectedMonth || setInternalSelectedMonth;
  const showEmailSubject = propShowEmailSubject !== undefined ? propShowEmailSubject : internalShowEmailSubject;
  const setShowEmailSubject = propSetShowEmailSubject || setInternalShowEmailSubject;
  const emailConfigOpen = propEmailConfigOpen !== undefined ? propEmailConfigOpen : internalEmailConfigOpen;
  const setEmailConfigOpen = propSetEmailConfigOpen || setInternalEmailConfigOpen;

  const [highlightsText, setHighlightsText] = useState("");

  const highlightLines = highlightsText
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const [kpiData, setKpiData] = useState({
    clientId: '',
    clientName: '',
    jobsMonitored: 0,
    jobsCompleted: 0,
    jobsFailed: 0,
    recoveredByClient: 0,
    recoveredByBFx: 0,
    caMissingPicks: 0,
    pkmsTicketsCount: 0,
    snowTicketsCount: 0,
  });

  const [failedJobsByCriticality, setFailedJobsByCriticality] = useState([]);
  const [recoveredJobsByCriticality, setRecoveredJobsByCriticality] = useState([]);
  const [snowTicketsData, setSnowTicketsData] = useState({
    ldapCount: 0,
    retailScheduleCount: 0,
    wofCount: 0,
  });

  // Helper function to get dynamic "Recovered by" label
  const getRecoveredByLabel = useCallback(() => {
    if (!kpiData.clientName) return "Recovered by Client";
    
    // Special case for Skechers (SKH)
    if (kpiData.clientId === 'SKH') {
      return "Recovered by Skechers";
    }
    
    // For all other clients, use their actual name
    return `Recovered by ${kpiData.clientName}`;
  }, [kpiData.clientId, kpiData.clientName]);

  // Generate dynamic title based on application selection
  const generateTitle = useCallback(() => {
    if (!selectedMonth) {
      return "GARPAC Monthly Status Report";
    }

    const [year, month] = selectedMonth.split('-');
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const monthName = monthNames[parseInt(month) - 1];
    
    // For GARPAC and OMEGA, keep the same title format
    let appName;
    if (selectedApplication === 'GARPAC' || selectedApplication === 'OMEGA' || selectedApplication === 'ALL') {
      appName = 'GARPAC';
    } else {
      // For other specific applications, use the application name
      appName = selectedApplication;
    }
    
    return `${appName} Monthly Status Report ${monthName} - ${year}`;
  }, [selectedMonth, selectedApplication]);

  // Update custom email subject when application or month changes
  useEffect(() => {
    const generateDefaultSubject = () => {
      if (!selectedMonth) {
        return "Monthly Report - GARPAC Status";
      }

      const [year, month] = selectedMonth.split('-');
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      const monthName = monthNames[parseInt(month) - 1];
      
      const appName = selectedApplication === 'GARPAC' ? 'GARPAC' : 
                     selectedApplication === 'OMEGA' ? 'OMEGA' : 
                     selectedApplication === 'ALL' ? 'GARPAC' : selectedApplication;
      
      return `Monthly Report - ${appName} Status - ${monthName} ${year}`;
    };

    const defaultSubject = generateDefaultSubject();
    setCustomEmailSubject(defaultSubject);
  }, [selectedApplication, selectedMonth]);

  /* 🔹 FETCH REPORT HIGHLIGHTS */
  useEffect(() => {
    const fetchReportHighlights = async () => {
      try {
        if (!selectedMonth) return;

        const params = new URLSearchParams({
          month: selectedMonth,
        });

        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }

        const data = await apiService.get(`/garpac-monthly-report/get-monthly-highlights?${params}`);
        setHighlightsText(data.highlights || "");
      } catch (error) {
        console.error("Error fetching report highlights:", error);
        setHighlightsText("");
      }
    };

    fetchReportHighlights();
  }, [selectedMonth, selectedApplication, selectedEnvironment]);

  /* 🔹 SAVE REPORT HIGHLIGHTS */
  const saveHighlights = async () => {
    try {
      if (!selectedMonth) return;

      const autoTitle = generateTitle();

      const payload = {
        title: autoTitle,
        highlights: highlightsText,
        month: selectedMonth,
      };

      if (selectedApplication && selectedApplication !== 'ALL') {
        payload.application = selectedApplication;
      }
      if (selectedEnvironment && selectedEnvironment !== 'ALL') {
        payload.env_id = selectedEnvironment;
      }

      await apiService.post("/garpac-monthly-report/save-monthly-highlights", payload);

      console.log("Highlights saved successfully");
      setEditMode(false);
    } catch (error) {
      console.error("Error saving highlights:", error);
    }
  };

  /* CLICK OUTSIDE */
  useEffect(() => {
    const handleClickOutside = async (e) => {
      if (
        editMode &&
        highlightRef.current &&
        !highlightRef.current.contains(e.target)
      ) {
        // Save when clicking outside
        try {
          if (selectedMonth) {
            const autoTitle = generateTitle();
            
            const payload = {
              title: autoTitle,
              highlights: highlightsText,
              month: selectedMonth,
            };

            if (selectedApplication && selectedApplication !== 'ALL') {
              payload.application = selectedApplication;
            }
            if (selectedEnvironment && selectedEnvironment !== 'ALL') {
              payload.env_id = selectedEnvironment;
            }
            
            await apiService.post("/garpac-monthly-report/save-monthly-highlights", payload);
            console.log("Highlights saved successfully");
          }
        } catch (error) {
          console.error("Error saving highlights:", error);
        }
        setEditMode(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editMode, highlightsText, selectedMonth, selectedApplication, selectedEnvironment, generateTitle]);

  // FETCH ALL KPIs
  useEffect(() => {
    const fetchAllKPIs = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedMonth) {
          params.append('month', selectedMonth);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-monthly-report/all-monthly-kpis";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setKpiData({
          clientId: data.clientId || '',
          clientName: data.clientName || '',
          jobsMonitored: data.jobsMonitored || 0,
          jobsCompleted: data.jobsCompleted || 0,
          jobsFailed: data.jobsFailed || 0,
          recoveredByClient: data.recoveredByClient || 0,
          recoveredByBFx: data.recoveredByBFx || 0,
          caMissingPicks: data.caMissingPicks || 0,
          pkmsTicketsCount: data.pkmsTicketsCount || 0,
          snowTicketsCount: data.snowTicketsCount || 0,
        });
      } catch (error) {
        console.error("Error fetching KPIs:", error);
      }
    };
    fetchAllKPIs();
  }, [selectedMonth, selectedApplication, selectedEnvironment]);

  // FETCH FAILED JOBS BY CRITICALITY
  useEffect(() => {
    const fetchFailedJobsByCriticality = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedMonth) {
          params.append('month', selectedMonth);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-monthly-report/failed-jobs-by-criticality";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setFailedJobsByCriticality(data || []);
      } catch (error) {
        console.error("Error fetching failed jobs by criticality:", error);
        setFailedJobsByCriticality([]);
      }
    };
    fetchFailedJobsByCriticality();
  }, [selectedMonth, selectedApplication, selectedEnvironment]);

  // FETCH RECOVERED JOBS BY CRITICALITY
  useEffect(() => {
    const fetchRecoveredJobsByCriticality = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedMonth) {
          params.append('month', selectedMonth);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-monthly-report/recovered-jobs-by-criticality";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setRecoveredJobsByCriticality(data || []);
      } catch (error) {
        console.error("Error fetching recovered jobs by criticality:", error);
        setRecoveredJobsByCriticality([]);
      }
    };
    fetchRecoveredJobsByCriticality();
  }, [selectedMonth, selectedApplication, selectedEnvironment]);

  // FETCH SNOW TICKETS DATA
  useEffect(() => {
    const fetchSnowTicketsData = async () => {
      try {
        const params = new URLSearchParams();
        if (selectedMonth) {
          params.append('month', selectedMonth);
        }
        if (selectedApplication && selectedApplication !== 'ALL') {
          params.append('application', selectedApplication);
        }
        if (selectedEnvironment && selectedEnvironment !== 'ALL') {
          params.append('env_id', selectedEnvironment);
        }
        
        let endpoint = "/garpac-monthly-report/snow-tickets-data";
        if (params.toString()) {
          endpoint += `?${params}`;
        }
        
        const data = await apiService.get(endpoint);
        setSnowTicketsData({
          ldapCount: data.ldapCount || 0,
          retailScheduleCount: data.retailScheduleCount || 0,
          wofCount: data.wofCount || 0,
        });
      } catch (error) {
        console.error("Error fetching SNOW tickets:", error);
        setSnowTicketsData({
          ldapCount: 0,
          retailScheduleCount: 0,
          wofCount: 0,
        });
      }
    };
    fetchSnowTicketsData();
  }, [selectedMonth, selectedApplication, selectedEnvironment]);

  const downloadDashboard = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `monthly-report-${selectedMonth}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  /* 🔹 SEND REPORT EMAIL */
  const sendReportEmail = async () => {
    if (!dashboardRef.current) return;
    
    setSendingEmail(true);
    
    try {
      const reportData = {
        highlights: highlightsText,
        kpis: kpiData
      };

      try {
        // First, try to send with image
        const canvas = await html2canvas(dashboardRef.current, {
          scale: 1.5,
          useCORS: true,
          quality: 0.8,
          backgroundColor: '#ffffff'
        });
        
        const imageData = canvas.toDataURL("image/jpeg", 0.8);

        const imageSizeKB = Math.round(imageData.length / 1024);
        console.log(`Image size: ${imageSizeKB}KB`);

        if (imageSizeKB > 5000) {
          throw new Error('Image too large, using text-only email');
        }

        const response = await apiService.post('/email/send-report', {
          reportType: 'monthly',
          reportData,
          imageData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || 'Monthly report sent successfully with image!',
          severity: 'success'
        });

      } catch (imageError) {
        console.log('Image email failed, trying text-only:', imageError.message);
        
        const response = await apiService.post('/email/send-report-text', {
          reportType: 'monthly',
          reportData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || 'Monthly report sent successfully (text format)!',
          severity: 'success'
        });
      }

    } catch (error) {
      console.error('Error sending email:', error);
      
      let errorMessage = 'Failed to send email. Please check your email configuration.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        
        if (errorMessage.includes('No email configuration found')) {
          errorMessage += ' Click the settings icon (⚙️) to configure email recipients.';
        } else if (errorMessage.includes('Email configuration is disabled')) {
          errorMessage = 'Email is configured but disabled. Click the settings icon (⚙️) to enable email sending.';
        }
      }
      
      setEmailStatus({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    downloadDashboard,
    sendEmail: sendReportEmail,
  }));

  // Prepare SNOW tickets pie chart data
  const snowTicketsPieData = [
    { name: 'LDAP', value: snowTicketsData.ldapCount, color: BLUE_PRIMARY },
    { name: 'Retail schedule', value: snowTicketsData.retailScheduleCount, color: ORANGE_ACCENT },
    { name: 'WOF', value: snowTicketsData.wofCount, color: '#8884d8' },
    { name: 'Other', value: snowTicketsData.otherCount, color: '#82ca9d' },
  ].filter(item => item.value > 0);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: LIGHT_BG,
        p: { xs: 1.5, md: 3 },
      }}
    >
      {/* Email Subject Input - Controlled from container */}
      {showEmailSubject && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Email Subject"
            value={customEmailSubject}
            onChange={(e) => setCustomEmailSubject(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
                '&:hover fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
                '&.Mui-focused fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
              },
              '& .MuiInputLabel-root': {
                color: BLUE_PRIMARY,
                '&.Mui-focused': {
                  color: BLUE_PRIMARY,
                },
              },
            }}
          />
        </Box>
      )}

      {/* Main Card */}
      <Paper
        ref={dashboardRef}
        sx={{
          border: `1px solid ${BORDER_GRAY}`,
          borderRadius: 1,
          bgcolor: WHITE_BG,
          p: { xs: 2, md: 3 },
          position: "relative",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography variant="h5" fontWeight="bold" color={BLUE_PRIMARY}>
            {generateTitle()}
          </Typography>
        </Box>

        {/* Top KPI Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 2,
            mb: 3,
          }}
        >
          {[
            { label: "Jobs monitored", value: kpiData.jobsMonitored },
            { label: "Jobs completed", value: kpiData.jobsCompleted },
            { label: "Jobs failed", value: kpiData.jobsFailed },
            { label: getRecoveredByLabel(), value: kpiData.recoveredByClient },
            { label: "Recovered by BFx", value: kpiData.recoveredByBFx },
            { label: "SNOW Tickets", value: kpiData.snowTicketsCount },
          ].map((item, idx) => (
            <Paper
              key={idx}
              elevation={3}
              sx={{
                bgcolor: BLUE_CARD,
                color: "white",
                p: 1.5,
                textAlign: "center",
                borderRadius: 2,
                minHeight: 70,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9, display: "block", mb: 0.5 }}>
                {item.label}
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {item.value}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Charts Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 3,
          }}
        >
          {/* Failed Jobs by Criticality */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color={BLUE_PRIMARY} mb={2} textAlign="center">
              Failed jobs by criticality
            </Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={failedJobsByCriticality}>
                  <XAxis dataKey="criticality" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={BLUE_PRIMARY}>
                    <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 'bold' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="caption" textAlign="center" display="block" mt={1}>
              Criticality
            </Typography>
          </Paper>

          {/* Recovered By Criticality (BFx only) */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color={BLUE_PRIMARY} mb={2} textAlign="center">
              Recovered By Criticality
            </Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recoveredJobsByCriticality}>
                  <XAxis dataKey="criticality" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill={ORANGE_ACCENT}>
                    <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 'bold' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="caption" textAlign="center" display="block" mt={1} sx={{ color: ORANGE_ACCENT }}>
              ● BFx
            </Typography>
          </Paper>

          {/* SNOW Tickets Breakdown */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, p: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" color={BLUE_PRIMARY} mb={2} textAlign="center">
              SNOW Tickets Breakdown
            </Typography>
            <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {snowTicketsPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={snowTicketsPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {snowTicketsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No tickets data
                </Typography>
              )}
            </Box>
            <Typography variant="caption" textAlign="center" display="block" mt={1}>
              LDAP • Retail schedule • WOF • Other
            </Typography>
          </Paper>
        </Box>

        {/* Bottom Row - CA Missing picks + PKMS */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 3,
          }}
        >
          {/* CA Missing picks */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, p: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" color={BLUE_PRIMARY} mb={2} textAlign="center">
              CA Missing picks
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 100 }}>
              <Typography variant="h2" fontWeight="bold" color={ORANGE_ACCENT}>
                {kpiData.caMissingPicks}
              </Typography>
            </Box>
          </Paper>

          {/* Pick ticket to PKMS */}
          <Paper sx={{ border: `1px solid ${BORDER_GRAY}`, p: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" color={BLUE_PRIMARY} mb={2} textAlign="center">
              # Pick ticket to PKMS
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 100 }}>
              <Typography variant="h2" fontWeight="bold" color={ORANGE_ACCENT}>
                {kpiData.pkmsTicketsCount}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Highlights Section - Editable */}
        <Box
          ref={highlightRef}
          onClick={() => !editMode && setEditMode(true)}
          sx={{
            border: `1px solid ${BORDER_GRAY}`,
            borderRadius: 1,
            p: 3,
            minHeight: 200,
            cursor: editMode ? "default" : "pointer",
          }}
        >
          <Typography variant="h6" fontWeight="bold" color={BLUE_PRIMARY} mb={2}>
            Highlights:
          </Typography>

          {!editMode ? (
            <Box sx={{ pl: 1 }}>
              {highlightLines.length === 0 ? (
                <Typography color="text.secondary" fontSize="0.95rem">
                  Click to add/edit highlights...
                </Typography>
              ) : (
                highlightLines.map((line, i) => (
                  <Typography key={i} sx={{ mb: 0.6, fontSize: "0.94rem" }}>
                    • {line}
                  </Typography>
                ))
              )}
            </Box>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                multiline
                minRows={5}
                maxRows={8}
                value={highlightsText}
                onChange={(e) => setHighlightsText(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2, "& .MuiInputBase-input": { fontSize: "0.93rem" } }}
              />
              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                <Button size="small" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={saveHighlights}
                  sx={{ bgcolor: BLUE_PRIMARY }}
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {/* Email Configuration Modal */}
      <EmailConfigModal
        reportType="monthly"
        open={emailConfigOpen}
        onClose={() => setEmailConfigOpen(false)}
      />

      {/* Email Status Snackbar */}
      <Snackbar
        open={emailStatus.open}
        autoHideDuration={6000}
        onClose={() => setEmailStatus({ ...emailStatus, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setEmailStatus({ ...emailStatus, open: false })} 
          severity={emailStatus.severity}
          sx={{ width: '100%' }}
        >
          {emailStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default MonthlyStatusDashboard;

